import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { Link } from 'react-scroll';
import * as THREE from 'three';
import { TrackballControls } from 'three/examples/jsm/controls/TrackballControls';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import { MeshSurfaceSampler } from 'three/examples/jsm/math/MeshSurfaceSampler';

// Star component for regular stars
const Star = ({ style }) => {
  return <div className="star" style={style}></div>;
};

// Shooting Star component
const ShootingStar = ({ style }) => {
  return <div className="shooting-star" style={style}></div>;
};

// Navigation Bar component
const NavBar = () => {
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['home', 'technologies', 'projects', 'about', 'connect'];
      const navbarHeight = 80;
      const scrollPosition = window.scrollY + window.innerHeight / 2;

      if (window.scrollY < 50) {
        setActiveSection('home');
        return;
      }

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const sectionTop = element.offsetTop - navbarHeight;
          const sectionBottom = sectionTop + element.offsetHeight;
          if (scrollPosition >= sectionTop && scrollPosition < sectionBottom) {
            setActiveSection(section);
            break;
          }
        }
      }

      if (window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 10) {
        setActiveSection('connect');
      }
    };

    handleScroll();

    let timeout;
    const debouncedHandleScroll = () => {
      clearTimeout(timeout);
      timeout = setTimeout(handleScroll, 100);
    };

    window.addEventListener('scroll', debouncedHandleScroll);

    return () => {
      window.removeEventListener('scroll', debouncedHandleScroll);
      clearTimeout(timeout);
    };
  }, []);

  return (
    <>
      <div className="navigation">
        <ul>
          {['home', 'technologies', 'projects', 'about', 'connect'].map((section) => (
            <li key={section} className={`list ${activeSection === section ? 'active' : ''}`}>
              <Link
                to={section}
                smooth={true}
                duration={500}
                spy={true}
                offset={-80}
                onSetActive={() => setActiveSection(section)}
                className="flex flex-col items-center justify-center"
              >
                <span className="icon">
                  <ion-icon name={section === 'home' ? 'home-outline' : 
                                 section === 'technologies' ? 'code-slash-outline' : 
                                 section === 'projects' ? 'briefcase-outline' : 
                                 section === 'about' ? 'person-outline' : 'chatbubble-outline'}></ion-icon>
                </span>
                <span className="text">{section.charAt(0).toUpperCase() + section.slice(1)}</span>
              </Link>
            </li>
          ))}
          <div className="indicator"></div>
        </ul>
      </div>

      <div className="mobile-navigation">
        <ul>
          {['home', 'technologies', 'projects', 'about', 'connect'].map((section) => (
            <li key={section} className={`list ${activeSection === section ? 'active' : ''}`}>
              <Link
                to={section}
                smooth={true}
                duration={500}
                spy={true}
                offset={-80}
                onSetActive={() => setActiveSection(section)}
              >
                <span className="icon">
                  <ion-icon name={section === 'home' ? 'home-outline' : 
                                 section === 'technologies' ? 'code-slash-outline' : 
                                 section === 'projects' ? 'briefcase-outline' : 
                                 section === 'about' ? 'person-outline' : 'chatbubble-outline'}></ion-icon>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
};

// Home Section
const HomeSection = () => {
  const canvasRef = useRef(null);
  const textRef = useRef(null);
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [animationDone, setAnimationDone] = useState(false);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const earthRef = useRef(null);
  const moonRef = useRef(null);
  const timeRef = useRef(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isVisible || !canvasRef.current) return;

    const scene = new THREE.Scene();
    const isMobile = window.innerWidth <= 768;
    const canvasWidth = isMobile ? window.innerWidth : window.innerWidth / 2;
    const canvasHeight = isMobile ? window.innerHeight * 0.6 : window.innerHeight;
    const aspectRatio = canvasWidth / canvasHeight;
    const camera = new THREE.PerspectiveCamera(75, aspectRatio, 0.1, 1000);
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(canvasWidth, canvasHeight);
    canvasRef.current.appendChild(renderer.domElement);

    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
    scene.add(ambientLight);
    const sunLight = new THREE.DirectionalLight(0xffffff, 1.2);
    sunLight.position.set(5, 3, 5);
    scene.add(sunLight);

    const textureLoader = new THREE.TextureLoader();
    const earthTexture = textureLoader.load(
      'https://threejs.org/examples/textures/planets/earth_atmos_2048.jpg',
      undefined,
      undefined,
      (err) => console.error('Failed to load earth texture:', err)
    );
    const moonTexture = textureLoader.load(
      'https://threejs.org/examples/textures/planets/moon_1024.jpg',
      undefined,
      undefined,
      (err) => console.error('Failed to load moon texture:', err)
    );

    const earthGeometry = new THREE.SphereGeometry(0.7, 64, 64);
    const earthMaterial = new THREE.MeshPhongMaterial({
      map: earthTexture,
      specular: new THREE.Color(0x555555),
      shininess: 50,
      emissive: new THREE.Color(0x004d99),
      emissiveIntensity: 0.3,
    });
    const earth = new THREE.Mesh(earthGeometry, earthMaterial);
    scene.add(earth);
    earthRef.current = earth;

    const moonGeometry = new THREE.SphereGeometry(0.18, 32, 32);
    const moonMaterial = new THREE.MeshPhongMaterial({
      map: moonTexture,
      specular: new THREE.Color(0x333333),
      shininess: 20,
      emissive: new THREE.Color(0x666666),
      emissiveIntensity: 0.2,
    });
    const moon = new THREE.Mesh(moonGeometry, moonMaterial);
    scene.add(moon);
    moonRef.current = moon;

    const asteroidGeometry = new THREE.SphereGeometry(0.05, 16, 16);
    const asteroidMaterial = new THREE.MeshPhongMaterial({ color: 0x888888 });
    const asteroids = [];
    for (let i = 0; i < 10; i++) {
      const asteroid = new THREE.Mesh(asteroidGeometry, asteroidMaterial);
      asteroid.position.set(
        Math.random() * 1.5,
        Math.random() * 0.5,
        Math.random() * 1.5
      );
      asteroid.userData = {
        angle: Math.random() * Math.PI * 2,
        speed: 0.02 + Math.random() * 0.03,
        radius: 1 + Math.random() * 0.5,
      };
      scene.add(asteroid);
      asteroids.push(asteroid);
    }

    camera.position.z = 2.5;
    camera.position.y = 0.3;
    camera.lookAt(0, 0, 0);

    sceneRef.current = scene;
    cameraRef.current = camera;
    rendererRef.current = renderer;

    const animate = () => {
      if (!sceneRef.current || !rendererRef.current || !cameraRef.current) return;

      requestAnimationFrame(animate);
      timeRef.current += 0.01;

      earthRef.current.rotation.y += 0.005;
      const orbitRadius = 1.2;
      moonRef.current.position.x = Math.cos(timeRef.current * 0.5) * orbitRadius;
      moonRef.current.position.z = Math.sin(timeRef.current * 0.5) * orbitRadius;
      moonRef.current.rotation.y += 0.01;

      asteroids.forEach((asteroid) => {
        asteroid.userData.angle += asteroid.userData.speed;
        asteroid.position.x = Math.cos(asteroid.userData.angle) * asteroid.userData.radius;
        asteroid.position.z = Math.sin(asteroid.userData.angle) * asteroid.userData.radius;
        asteroid.position.y = Math.sin(asteroid.userData.angle * 2) * 0.2;
      });

      rendererRef.current.render(sceneRef.current, cameraRef.current);
    };

    animate();

    const handleResize = () => {
      if (cameraRef.current && rendererRef.current) {
        const isMobile = window.innerWidth <= 768;
        const canvasWidth = isMobile ? window.innerWidth : window.innerWidth / 2;
        const canvasHeight = isMobile ? window.innerHeight * 0.6 : window.innerHeight;
        cameraRef.current.aspect = canvasWidth / canvasHeight;
        cameraRef.current.updateProjectionMatrix();
        rendererRef.current.setSize(canvasWidth, canvasHeight);
      }
    };
    window.addEventListener('resize', handleResize);

    return () => {
      if (rendererRef.current) {
        rendererRef.current.dispose();
        if (canvasRef.current) {
          canvasRef.current.removeChild(rendererRef.current.domElement);
        }
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [isVisible]);

  useEffect(() => {
    if (isVisible && textRef.current) {
      const animationDuration = 3000;
      const element = textRef.current;
      const checkAnimation = () => {
        const computedStyle = window.getComputedStyle(element);
        const animationName = computedStyle.getPropertyValue('animation-name');
        if (animationName.includes('typing') && !element.classList.contains('cursor-hidden')) {
          const animationDurationMs = parseFloat(computedStyle.getPropertyValue('animation-duration')) * 1000;
          setTimeout(() => {
            setAnimationDone(true);
          }, animationDurationMs);
        }
      };
      checkAnimation();
    }
  }, [isVisible]);

  return (
    <div ref={sectionRef} id="home" className={`section flex flex-row min-h-screen ${isVisible ? 'visible' : ''}`}>
      <section id="home-firsthalf" className="w-1/2 flex flex-col items-center justify-center text-center">
        <h1
          ref={textRef}
          className={`animated-text text-5xl md:text-6xl font-bold mb-4 ${animationDone ? 'cursor-hidden' : ''}`}
          style={{ position: 'relative' }}
        >
          Hi, I'm Rajnish
        </h1>
        <p className="text-xl md:text-2xl text-gray-300 max-w-2xl mx-auto">
          Lost in code.<br />
          Orbiting bold ideas.<br />
          Fuelled by curiosity, powered by code, built for impact.
        </p>
        <Link
          to="projects"
          smooth={true}
          duration={500}
          className="mt-8 inline-block px-6 py-3 bg-blue-500 rounded-lg hover:bg-blue-600 text-lg font-semibold cursor-pointer"
        >
          Explore My Work
        </Link>
      </section>
      <section id="home-secondhalf" className="w-1/2 min-h-screen flex items-center justify-center">
        <div ref={canvasRef} style={{ width: '100%', height: '100%' }}></div>
      </section>
    </div>
  );
};

// Technologies Section
const TechnologiesSection = () => {
  const canvasRef = useRef(null);
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const vortexGroupRef = useRef(null);
  const particlesRef = useRef(null);
  const particleDataRef = useRef([]);
  const timeRef = useRef(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isVisible || !canvasRef.current) return;

    const scene = new THREE.Scene();
    const isMobile = window.innerWidth <= 768;
    const canvasWidth = window.innerWidth;
    const canvasHeight = isMobile ? window.innerHeight * 0.5 : window.innerHeight * 0.8;
    const camera = new THREE.PerspectiveCamera(75, canvasWidth / canvasHeight, 0.1, 1000);
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(canvasWidth, canvasHeight);
    canvasRef.current.appendChild(renderer.domElement);

    const ambientLight = new THREE.AmbientLight(0xffffff, 1);
    scene.add(ambientLight);

    const vortexGroup = new THREE.Group();
    vortexGroup.position.x = isMobile ? 0 : -3;
    vortexGroup.position.y = isMobile ? -2.0 : 0;

    for (let i = 0; i < 3; i++) {
      const innerRadius = 0.3 + i * 0.3;
      const outerRadius = innerRadius + 0.5;
      const diskGeometry = new THREE.RingGeometry(innerRadius, outerRadius, 128);
      const diskMaterial = new THREE.ShaderMaterial({
        uniforms: {
          time: { value: 0 },
          innerGlow: { value: new THREE.Color(0x66ccff) },
          outerGlow: { value: new THREE.Color(0x003366) },
          layerOffset: { value: i * 0.5 }
        },
        vertexShader: `
          varying vec2 vUv;
          void main() {
            vUv = uv;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `,
        fragmentShader: `
          uniform float time;
          uniform vec3 innerGlow;
          uniform vec3 outerGlow;
          uniform float layerOffset;
          varying vec2 vUv;
          void main() {
            float swirl = sin(vUv.x * 40.0 + time * 5.0 + layerOffset) * 0.3;
            float intensity = 0.5 + 0.5 * sin(vUv.y * 3.1415 + swirl);
            vec3 color = mix(outerGlow, innerGlow, intensity);
            float dist = length(vUv - vec2(0.5));
            float alpha = 0.8 - smoothstep(0.2, 0.7, dist);
            gl_FragColor = vec4(color, alpha * 0.9);
          }
        `,
        side: THREE.DoubleSide,
        transparent: true,
        depthWrite: false
      });
      const disk = new THREE.Mesh(diskGeometry, diskMaterial);
      disk.rotation.x = Math.PI / 2;
      vortexGroup.add(disk);
    }
    scene.add(vortexGroup);

    const coreGeometry = new THREE.CircleGeometry(0.2, 64);
    const coreMaterial = new THREE.ShaderMaterial({
      uniforms: { time: { value: 0 } },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform float time;
        varying vec2 vUv;
        void main() {
          float dist = length(vUv - vec2(0.5));
          float glow = 1.0 - smoothstep(0.0, 0.5, dist);
          vec3 color = vec3(0.9, 0.95, 1.0) * glow;
          gl_FragColor = vec4(color, glow * 0.9);
        }
      `,
      transparent: true,
      depthWrite: false
    });
    const core = new THREE.Mesh(coreGeometry, coreMaterial);
    core.rotation.x = Math.PI / 2;
    vortexGroup.add(core);

    const technologies = [
      { name: 'HTML', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/html5/html5-original.svg', borderColor: '#ff5733' },
      { name: 'CSS', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/css3/css3-original.svg', borderColor: '#33aaff' },
      { name: 'JS', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg', borderColor: '#f7df1e' },
      { name: 'Java', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg', borderColor: '#ff2e63' },
      { name: 'C', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/c/c-original.svg', borderColor: '#00599c' },
      { name: 'C++', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/cplusplus/cplusplus-original.svg', borderColor: '#004482' },
      { name: 'Python', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg', borderColor: '#3776ab' },
      { name: 'Node', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg', borderColor: '#68a063' },
      { name: 'Express', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/express/express-original.svg', borderColor: '#aaaaaa' },
      { name: 'React', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg', borderColor: '#61dafb' },
      { name: 'MongoDB', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg', borderColor: '#47a248' },
      { name: 'MySQL', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mysql/mysql-original.svg', borderColor: '#00758f' },
      { name: 'SpringBoot', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/spring/spring-original.svg', borderColor: '#6db33f' }
    ];

    const particles = new THREE.Group();
    const particleData = [];

    function createLogoWithBorder(imageUrl, borderColor, callback) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () {
        const canvas = document.createElement('canvas');
        canvas.width = 256;
        canvas.height = 256;
        const ctx = canvas.getContext('2d');

        const gradient = ctx.createRadialGradient(128, 128, 40, 128, 128, 128);
        gradient.addColorStop(0, 'rgba(255, 255, 255, 0.6)');
        gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(128, 128, 128, 0, Math.PI * 2);
        ctx.fill();

        ctx.strokeStyle = borderColor;
        ctx.lineWidth = 10;
        ctx.beginPath();
        ctx.arc(128, 128, 110, 0, Math.PI * 2);
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(128, 128, 100, 0, Math.PI * 2);
        ctx.clip();

        ctx.drawImage(img, 64, 64, 128, 128);

        const texture = new THREE.CanvasTexture(canvas);
        callback(texture);
      };
      img.onerror = () => console.error(`Failed to load image: ${imageUrl}`);
      img.src = imageUrl;
    }

    let loadedCount = 0;
    technologies.forEach((tech, index) => {
      createLogoWithBorder(tech.logo, tech.borderColor, texture => {
        const material = new THREE.SpriteMaterial({ map: texture, transparent: true });
        const sprite = new THREE.Sprite(material);
        sprite.scale.set(isMobile ? 0.8 : 1.2, isMobile ? 0.8 : 1.2, 1);
        sprite.position.set(vortexGroup.position.x, 0, 0);
        sprite.userData = {
          angle: Math.random() * Math.PI * 2,
          radius: 0.1,
          angularSpeed: Math.random() * 0.05 + 0.05,
          radialSpeed: 0.02 + Math.random() * 0.02,
          aligning: false,
          targetX: 0,
          targetY: 0,
          targetZ: 0,
          life: 100 + Math.random() * 50,
          tech: tech
        };
        particles.add(sprite);
        particleData.push(sprite);
        loadedCount++;
        if (loadedCount === technologies.length) {
          scene.add(particles);
          animate();
        }
      });
    });

    camera.position.z = isMobile ? 4 : 5;
    camera.position.y = isMobile ? 0.5 : 1;
    camera.lookAt(0, 0, 0);

    sceneRef.current = scene;
    cameraRef.current = camera;
    rendererRef.current = renderer;
    vortexGroupRef.current = vortexGroup;
    particlesRef.current = particles;
    particleDataRef.current = particleData;

    function animate() {
      if (!sceneRef.current || !rendererRef.current || !cameraRef.current) return;

      requestAnimationFrame(animate);
      timeRef.current += 0.03;

      vortexGroupRef.current.rotation.y += 0.03;

      vortexGroupRef.current.children.forEach(child => {
        if (child.material.uniforms?.time) {
          child.material.uniforms.time.value = timeRef.current;
        }
      });

      particleDataRef.current.forEach((p, i) => {
        if (!p.userData.aligning) {
          p.userData.angle += p.userData.angularSpeed;
          p.userData.radius += p.userData.radialSpeed;
          p.position.x = Math.cos(p.userData.angle) * p.userData.radius + vortexGroupRef.current.position.x;
          p.position.z = Math.sin(p.userData.angle) * p.userData.radius;
          p.position.y = Math.sin(p.userData.angle * 2) * 0.1;
          p.userData.life -= 1;

          if (p.userData.radius > 2.5 || p.userData.life <= 0) {
            p.userData.aligning = true;
            const alignedCount = particleDataRef.current.filter(p => p.userData.aligning).length - 1;
            const itemsPerRow = isMobile ? 3 : 4;
            const row = Math.floor(alignedCount / itemsPerRow);
            const col = alignedCount % itemsPerRow;
            const offsetX = isMobile ? -1.5 : 0.5;
            p.userData.targetX = offsetX + col * (isMobile ? 1.0 : 1.5);
            p.userData.targetY = isMobile ? 1.5 - row * 1.0 : 1 - row * 1.5;
            p.userData.targetZ = 0;
          }
        } else {
          p.position.x += (p.userData.targetX - p.position.x) * 0.05;
          p.position.y += (p.userData.targetY - p.position.y) * 0.05;
          p.position.z += (p.userData.targetZ - p.position.z) * 0.05;
        }
        p.lookAt(cameraRef.current.position);
      });

      rendererRef.current.render(sceneRef.current, cameraRef.current);
    }

    window.addEventListener('resize', () => {
      if (cameraRef.current && rendererRef.current) {
        const isMobile = window.innerWidth <= 768;
        const canvasWidth = window.innerWidth;
        const canvasHeight = isMobile ? window.innerHeight * 0.5 : window.innerHeight * 0.8;
        cameraRef.current.aspect = canvasWidth / canvasHeight;
        cameraRef.current.updateProjectionMatrix();
        rendererRef.current.setSize(canvasWidth, canvasHeight);

        vortexGroupRef.current.position.x = isMobile ? 0 : -3;
        vortexGroupRef.current.position.y = isMobile ? -2.0 : 0;
        cameraRef.current.position.z = isMobile ? 4 : 5;
        cameraRef.current.position.y = isMobile ? 0.5 : 1;
        particleDataRef.current.forEach((p, i) => {
          if (p.userData.aligning) {
            const alignedCount = particleDataRef.current.filter(p => p.userData.aligning).indexOf(p);
            const itemsPerRow = isMobile ? 3 : 4;
            const row = Math.floor(alignedCount / itemsPerRow);
            const col = alignedCount % itemsPerRow;
            const offsetX = isMobile ? -1.5 : 0.5;
            p.userData.targetX = offsetX + col * (isMobile ? 1.0 : 1.5);
            p.userData.targetY = isMobile ? 1.5 - row * 1.0 : 1 - row * 1.5;
          }
        });
      }
    });

    return () => {
      if (rendererRef.current) {
        rendererRef.current.dispose();
        if (canvasRef.current) {
          canvasRef.current.removeChild(rendererRef.current.domElement);
        }
      }
      window.removeEventListener('resize', () => {});
    };
  }, [isVisible]);

  return (
    <section ref={sectionRef} id="technologies" className={`section flex flex-col items-center py-8 ${isVisible ? 'visible' : ''}`}>
      <h2 className="text-4xl font-bold mb-4 text-center">Technologies I Worked On</h2>
      <div ref={canvasRef} style={{ width: '100%', height: '80vh', zIndex: 1 }}></div>
    </section>
  );
};

// Projects Section
const ProjectsSection = () => {
  const projects = [
    {
      title: 'Space Portfolio',
      description: 'A cosmic-themed portfolio showcasing my skills with React and Tailwind CSS.',
      image: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcR8QaxxLGeggrHjc-YS1CXFxdFOBCj5TpepCg&s',
      link: '#',
    },
    {
      title: 'E-Commerce Platform',
      description: 'A fully functional online store with payment integration and authentication.',
      image: 'https://images.ctfassets.net/wowgx05xsdrr/2q1LgDWGbCJ7tN1ewQX8En/205f52600f54838e59138e763211e2c5/CD4323_Inventory-Management_Apr-2022_V2_Header.jpg',
      link: 'https://github.com/rajnisht7/store_skeleton',
    },
    {
      title: 'Web Based Search Bar',
      description: 'A Web Based Search Bar that directly search in browser.',
      image: 'https://raw.githubusercontent.com/rajnisht7/PopSearch/refs/heads/main/screenshots/search_bar_2.png',
      link: 'https://github.com/rajnisht7/PopSearch',
    },
    {
      title: 'Multi-Threaded Proxy WebServer',
      description: 'A high-performance, multithreaded HTTP/HTTPS proxy server implemented in C',
      image: 'https://marvel-b1-cdn.bc0a.com/f00000000310757/www.fortinet.com/content/dam/fortinet/images/cyberglossary/proxy-server-1.jpeg',
      link: 'https://github.com/rajnisht7/Multithreaded_Proxy_Web_Server',
    },
    {
      title: 'LevelUp-Gaming Website',
      description: 'A Gaming Website where all games are present at one place',
      image: 'https://raw.githubusercontent.com/rajnisht7/LevelUp/refs/heads/main/screenshot/image1.png',
      link: 'https://levelup-beta.vercel.app/',
    }
  ];

  const [visibleCards, setVisibleCards] = useState({});
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleCards((prev) => ({
              ...prev,
              [entry.target.dataset.index]: true,
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.project-card').forEach((card, index) => {
      card.dataset.index = index;
      observer.observe(card);
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (window.VanillaTilt) {
      window.VanillaTilt.init(document.querySelectorAll(".project-card"), {
        max: 15,
        speed: 400,
        glare: true,
        "max-glare": 0.5,
      });
    } else {
      console.warn('VanillaTilt is not loaded. Ensure the library is included in your HTML.');
    }
  }, []);

  const ProjectCard = ({ project, isVisible }) => (
    <div className="project-card bg-gray-800 bg-opacity-50 rounded-lg overflow-hidden">
      <img src={project.image} alt={project.title} className="w-full h-48 object-cover" />
      <div className="p-6">
        <h3 className="text-2xl font-semibold mb-2">{project.title}</h3>
        <p className="text-gray-300 mb-4">{project.description}</p>
        <a href={project.link} className="text-blue-400 hover:underline">
          View Project
        </a>
      </div>
    </div>
  );

  return (
    <section ref={sectionRef} id="projects" className={`section min-h-screen flex flex-col items-center justify-center py-16 ${isVisible ? 'visible' : ''}`}>
      <h2 className="text-4xl font-bold mb-8 text-center">My Projects</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl">
        {projects.map((project, index) => (
          <ProjectCard
            key={index}
            project={project}
            isVisible={visibleCards[index]}
          />
        ))}
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      id="about"
      className={`section min-h-screen flex flex-col items-center justify-center py-16 ${isVisible ? 'visible' : ''}`}
    >
      <h2 className="text-4xl font-bold mb-8 text-center text-white">About Me</h2>
      <p className="text-lg text-center max-w-2xl mx-auto text-gray-300">
  I'm a tech enthusiast with a fire for innovation and problem-solving. I completed my schooling at Kendriya Vidyalaya Sangathan, where my love for tech first sparked. Now, I'm chasing my B.Tech at Guru Gobind Singh Indraprastha University, diving deep into the world of coding and cutting-edge tech.<br/><br/>I thrive in the thrill of competition—I've clinched first place in a coding contest, conquered a hackathon, and even organized a tech event that brought together brilliant minds. Leading a dynamic team of 25, I’m all about collaboration, creativity, and pushing boundaries. Whether it's crafting clean code or sparking ideas that shape the future, I’m here to make things happen!
      </p>
    </section>
  );
};

// Connect Section
const ConnectSection = () => {
  const socialLinks = [
    { name: 'logo-github', url: 'https://github.com/rajnisht7' },
    { name: 'logo-linkedin', url: 'https://www.linkedin.com/in/rajnishtiwari1' },
    { name: 'logo-twitter', url: 'https://x.com/rajnisht7' },
  ];

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const sectionRef = useRef(null);
  const canvasRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const groupRef = useRef(null);
  const subgroupsRef = useRef([]);
  const pathsRef = useRef([]);
  const samplerRef = useRef(null);
  const earthRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isVisible || !canvasRef.current) return;

    const scene = new THREE.Scene();
    const isMobile = window.innerWidth <= 768;
    const canvasWidth = isMobile ? window.innerWidth : window.innerWidth / 2;
    const canvasHeight = isMobile ? window.innerHeight * 0.5 : window.innerHeight * 0.6;
    const camera = new THREE.PerspectiveCamera(75, canvasWidth / canvasHeight, 0.1, 1000);
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(canvasWidth, canvasHeight);
    canvasRef.current.appendChild(renderer.domElement);

    camera.position.z = 250;
    camera.position.y = 100;

    const controls = new TrackballControls(camera, renderer.domElement);
    controls.noPan = true;
    controls.maxDistance = 600;
    controls.minDistance = 150;
    controls.rotateSpeed = 2;

    const group = new THREE.Group();
    group.rotation.y = 2;
    scene.add(group);

    let airplane = new THREE.Group();
    new OBJLoader().load(
      'https://assets.codepen.io/127738/Airplane_model2.obj',
      (obj) => {
        airplane = obj;
        const mat = new THREE.MeshPhongMaterial({
          emissive: 0xffffff,
          emissiveIntensity: 0.3,
        });
        airplane.children.forEach((child) => {
          child.geometry.scale(0.013, 0.013, 0.013);
          child.geometry.translate(0, 122, 0);
          child.material = mat;
        });
        const angles = [0.3, 1.3, 2.14, 2.6];
        const speeds = [0.008, 0.01, 0.014, 0.02];
        const rotations = [0, 2.6, 1.5, 4];
        for (let i = 0; i < 4; i++) {
          const g = new THREE.Group();
          g.speed = speeds[i];
          subgroupsRef.current.push(g);
          group.add(g);
          const g2 = new THREE.Group();
          let _airplane = airplane.clone();
          g.add(g2);
          g2.add(_airplane);
          g2.rotation.x = rotations[i];
          g.rotation.y = angles[i];
          g.reverse = i < 2;
          if (i < 2) {
            _airplane.children[0].geometry = airplane.children[0].geometry.clone().rotateY(Math.PI);
          }
        }
      },
      undefined,
      (err) => console.error('Failed to load airplane model:', err)
    );

    new OBJLoader().load(
      'https://assets.codepen.io/127738/NOVELO_EARTH.obj',
      (obj) => {
        earthRef.current = obj.children[0];
        earthRef.current.geometry.scale(0.35, 0.35, 0.35);
        earthRef.current.geometry.translate(0, -133, 0);

        let positions = Array.from(earthRef.current.geometry.attributes.position.array).splice(0, 3960 * 3);
        const landGeom = new THREE.BufferGeometry();
        landGeom.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        const land = new THREE.Mesh(landGeom);

        positions = Array.from(earthRef.current.geometry.attributes.position.array).splice(3960 * 3, 540 * 3);
        const waterGeom = new THREE.BufferGeometry();
        waterGeom.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        waterGeom.computeVertexNormals();
        const waterMat = new THREE.MeshLambertMaterial({ color: 0x0da9c3, transparent: true, opacity: 1 });
        const water = new THREE.Mesh(waterGeom, waterMat);
        group.add(water);

        const light = new THREE.HemisphereLight(0xccffff, 0x000033, 1);
        scene.add(light);

        samplerRef.current = new MeshSurfaceSampler(land).build();

        for (let i = 0; i < 24; i++) {
          const path = new Path();
          pathsRef.current.push(path);
          group.add(path.line);
        }

        renderer.setAnimationLoop(animate);
      },
      (xhr) => console.log((xhr.loaded / xhr.total) * 100 + '% loaded'),
      (err) => console.error('Failed to load earth model:', err)
    );

    const tempPosition = new THREE.Vector3();
    const lineMaterial = new THREE.LineBasicMaterial({ color: 0xbbde2d, transparent: true, opacity: 0.6 });
    class Path {
      constructor() {
        this.geometry = new THREE.BufferGeometry();
        this.line = new THREE.Line(this.geometry, lineMaterial);
        this.vertices = [];

        samplerRef.current.sample(tempPosition);
        this.previousPoint = tempPosition.clone();
      }
      update() {
        let pointFound = false;
        while (!pointFound && samplerRef.current) {
          samplerRef.current.sample(tempPosition);
          if (tempPosition.distanceTo(this.previousPoint) < 12) {
            this.vertices.push(tempPosition.x, tempPosition.y, tempPosition.z);
            this.previousPoint = tempPosition.clone();
            pointFound = true;
          }
        }
        this.geometry.setAttribute('position', new THREE.Float32BufferAttribute(this.vertices, 3));
      }
    }

    function animate() {
      if (!sceneRef.current || !rendererRef.current || !cameraRef.current) return;

      group.rotation.y += 0.001;

      subgroupsRef.current.forEach((g) => {
        g.children[0].rotation.x += (g.speed * (g.reverse ? -1 : 1));
      });

      pathsRef.current.forEach((path) => {
        if (path.vertices.length < 35000) {
          path.update();
        }
      });

      controls.update();
      rendererRef.current.render(sceneRef.current, cameraRef.current);
    }

    const handleResize = () => {
      if (cameraRef.current && rendererRef.current) {
        const isMobile = window.innerWidth <= 768;
        const canvasWidth = isMobile ? window.innerWidth : window.innerWidth / 2;
        const canvasHeight = isMobile ? window.innerHeight * 0.5 : window.innerHeight * 0.6;
        cameraRef.current.aspect = canvasWidth / canvasHeight;
        cameraRef.current.updateProjectionMatrix();
        rendererRef.current.setSize(canvasWidth, canvasHeight);
      }
    };
    window.addEventListener('resize', handleResize);

    sceneRef.current = scene;
    cameraRef.current = camera;
    rendererRef.current = renderer;
    groupRef.current = group;

    return () => {
      if (rendererRef.current) {
        rendererRef.current.setAnimationLoop(null);
        rendererRef.current.dispose();
        if (canvasRef.current) {
          canvasRef.current.removeChild(rendererRef.current.domElement);
        }
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [isVisible]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      setError('Please fill out all fields.');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch('https://formsubmit.co/ajax/tssraj4102@gmail.com', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          message: formData.message,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setIsSubmitted(true);
        setError('');
        setFormData({ name: '', email: '', message: '' });
      } else {
        setError('Failed to send message. Please try again later.');
      }
    } catch (err) {
      setError('An error occurred. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section ref={sectionRef} id="connect" className={`section min-h-screen flex flex-col items-center justify-center py-16 ${isVisible ? 'visible' : ''}`}>
      <h2 className="text-4xl font-bold mb-8 text-center">Let's Connect</h2>
      <div className="flex space-x-6 mb-8">
        {socialLinks.map((link) => (
          <a
            key={link.name}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-4xl social-icon"
          >
            <ion-icon name={link.name}></ion-icon>
          </a>
        ))}
      </div>
      <div className="w-full max-w-6xl flex flex-col md:flex-row items-center justify-between">
        <div className="w-full md:w-1/2 max-w-md mb-8 md:mb-0">
          {isSubmitted ? (
            <div className="bg-gray-800 bg-opacity-50 p-6 rounded-lg text-center">
              <h3 className="text-2xl font-semibold text-green-400 mb-4">Message Sent!</h3>
              <p className="text-gray-300">Thank you for reaching out. I'll get back to you soon!</p>
            </div>
          ) : (
            <div className="bg-gray-800 bg-opacity-50 p-6 rounded-lg">
              {error && (
                <p className="text-red-400 mb-4 text-center">{error}</p>
              )}
              <form onSubmit={handleSubmit}>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your Name"
                  className="w-full p-3 mb-4 bg-gray-900 rounded text-white"
                />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Your Email"
                  className="w-full p-3 mb-4 bg-gray-900 rounded text-white"
                />
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Your Message"
                  className="w-full p-3 mb-4 bg-gray-900 rounded text-white"
                  rows="4"
                ></textarea>
                <input
                  type="hidden"
                  name="_subject"
                  value="New Contact Form Submission"
                />
                <input
                  type="hidden"
                  name="_template"
                  value="table"
                />
                <button
                  type="submit"
                  className="w-full p-3 bg-blue-500 rounded hover:bg-blue-600"
                  disabled={isLoading}
                >
                  {isLoading ? <div className="spinner"></div> : 'Send Message'}
                </button>
              </form>
            </div>
          )}
        </div>
        <div className="w-full md:w-1/2 flex items-center justify-center">
          <div ref={canvasRef} style={{ width: '100%', height: '60vh' }}></div>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="footer bg-gray-900 py-8">
      <div className="max-w-6xl mx-auto px-4 flex flex-col md:flex-row justify-between items-center">
        <div className="mb-4 md:mb-0">
          <p className="text-gray-400">Thank You For Visiting!</p>
        </div>
        <div className="flex space-x-6">
          <Link to="home" smooth={true} duration={500} className="text-gray-400 hover:text-white cursor-pointer">
            Home
          </Link>
          <Link
            to="technologies"
            smooth={true}
            duration={500}
            className="text-gray-400 hover:text-white cursor-pointer"
          >
            Technologies
          </Link>
          <Link to="projects" smooth={true} duration={500} className="text-gray-400 hover:text-white cursor-pointer">
            Projects
          </Link>
          <Link to="about" smooth={true} duration={500} className="text-gray-400 hover:text-white cursor-pointer">
            About me
          </Link>
          <Link to="connect" smooth={true} duration={500} className="text-gray-400 hover:text-white cursor-pointer">
            Connect
          </Link>
        </div>
      </div>
      <div className="mt-4 text-center text-gray-500">
        © {new Date().getFullYear()} All rights reserved.
      </div>
    </footer>
  );
};

// Main App component
const App = () => {
  const generateStars = (numStars) => {
    const stars = [];
    for (let i = 0; i < numStars; i++) {
      const style = {
        left: `${Math.random() * 100}vw`,
        top: `${Math.random() * 100}vh`,
        animationDuration: `${Math.random() * 10 + 5}s`,
        animationDelay: `${Math.random() * 5}s`,
        '--random-x': Math.random() * 2 - 1,
        '--random-y': Math.random() * 2 - 1,
      };
      stars.push(<Star key={`star-${i}`} style={style} />);
    }
    return stars;
  };

  const generateShootingStars = (numShootingStars) => {
    const shootingStars = [];
    for (let i = 0; i < numShootingStars; i++) {
      const style = {
        left: `${Math.random() * 100}vw`,
        top: `${Math.random() * 100}vh`,
        animationDuration: `${Math.random() * 1.5 + 1}s`,
        animationDelay: `${Math.random() * 5 + 2}s`,
        '--shoot-x': (Math.random() * 2 - 1) * 50,
        '--shoot-y': (Math.random() * 2 - 1) * 50,
      };
      shootingStars.push(<ShootingStar key={`shooting-star-${i}`} style={style} />);
    }
    return shootingStars;
  };

  useEffect(() => {
    const nebula = document.querySelector('.nebula');
    if (nebula) {
      const handleMouseMove = (e) => {
        const x = (e.clientX / window.innerWidth - 0.5) * 20;
        const y = (e.clientY / window.innerHeight - 0.5) * 20;
        nebula.style.backgroundPosition = `${50 + x}% ${50 + y}%`;
      };
      window.addEventListener('mousemove', handleMouseMove);
      return () => window.removeEventListener('mousemove', handleMouseMove);
    }
  }, []);

  return (
    <div className="bg-gradient-to-br from-black via-gray-950 to-blue-950 text-white">
      <div className="nebula"></div>
      <div className="stars z-0">
        {generateStars(50)}
        {generateShootingStars(8)}
      </div>
      <NavBar />
      <HomeSection />
      <TechnologiesSection />
      <ProjectsSection />
      <AboutSection />
      <ConnectSection />
      <Footer />
    </div>
  );
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<App />);
